import { UIKit } from "@/shared/ui";
/**
 * Виджет «Свойства» — правая панель редактора.
 *
 * Показывает параметры выделенного объекта (стена / проём / угол / прибор / светильник /
 * кабель) и позволяет задать их числом, а не мышью: для чертежа это основной способ ввода.
 * У выключателя здесь видно, какие светильники он зажигает, у светильника — какой
 * выключатель его включает: группа освещения читается из проводки, а не хранится.
 * Не хранит паспорт проекта и экспликацию: это отдельное меню верхней панели.
 */
import { cableLength } from "@/widgets/plan";
import { CATALOG, cableItemById, type CatalogItem } from "@/shared/catalog";
import { cableEndCatalogItem } from "@/widgets/electrical";
import { SelectionKind, useEditorStore } from "@/widgets/editor";
import { useProjectStore } from "@/shared/store";
import { useWiringStore } from "@/shared/store/layers";
import { remove } from "@/shared/store/actions";
import { formatMeters } from "@/shared/lib/geometry";

export type PropertiesPanelCatalogItem = CatalogItem;

export const CableProperties = () => {
	const layers = useProjectStore((s) => s.project.layers);
	const graph = layers.walls;
	const selection = useEditorStore((s) => s.selection);
	const setSelection = useEditorStore((s) => s.setSelection);
	const pushHistory = useProjectStore((s) => s.pushHistory);
	const updateCable = useWiringStore((s) => s.updateCable);

	// Хелпер для updateFixture в зависимости от каталога

	const onDelete = () => {
		// Удаление кнопкой отменяется по Ctrl+Z, как и клавишей Delete
		pushHistory();
		remove(selection);
		setSelection(null);
	};

	if (!selection) return null;
	if (selection.kind === SelectionKind.Cable) {
		const cable = layers.wiring.items[selection.id];
		if (!cable) return null;
		const nameOf = (id: string) => cableEndCatalogItem(layers.electrical, id)?.name ?? "Прибор";
		const length = cableLength(graph, layers.electrical, cable);
		const material = cable.material ? cableItemById(cable.material) : null;
		return (
			<>
				<UIKit.Text className="text-sm font-semibold tracking-label text-ink uppercase">
					Кабель
				</UIKit.Text>
				<div className="flex flex-col gap-1 rounded-lg bg-slate-50 px-2.5 py-2 text-xs text-ink">
					<UIKit.Text as="span">
						От: <b className="text-ink">{nameOf(cable.from)}</b>
					</UIKit.Text>
					<UIKit.Text as="span">
						До: <b className="text-ink">{nameOf(cable.to)}</b>
					</UIKit.Text>
				</div>
				<UIKit.Select
					label="Группа питания"
					value={cable.groupId && layers.wiring.groups[cable.groupId] ? cable.groupId : ""}
					options={[
						{ value: "", label: "Не назначена" },
						...Object.values(layers.wiring.groups).map((group) => ({
							value: group.id,
							label: group.name,
						})),
					]}
					onChange={(groupId) => {
						if ((cable.groupId ?? "") === groupId) return;
						useProjectStore
							.getState()
							.recordEdit(() => updateCable(cable.id, { groupId: groupId || undefined }));
					}}
				/>
				<div className="flex flex-col gap-1">
					<UIKit.Text as="span" className="text-xs font-semibold tracking-label text-ink uppercase">
						Марка кабеля
					</UIKit.Text>
					{!material && (
						<UIKit.Text className="text-xs text-amber-700">Марка не указана</UIKit.Text>
					)}
					{CATALOG.cable.items.map((item) => (
						<UIKit.Button
							key={item.id}
							type="button"
							title={item.description}
							className={`flex items-center gap-2 rounded-md border border-transparent px-2 py-1.5 text-left text-xs text-ink hover:bg-accent-soft${material?.id === item.id ? " border-accent bg-accent-soft font-medium text-accent" : ""}`}
							onClick={() => {
								if (material?.id === item.id) return;
								pushHistory();
								updateCable(cable.id, { material: item.id });
							}}
						>
							<UIKit.Text
								as="span"
								className="size-2.5 shrink-0 rounded-sm"
								style={{ background: item.color }}
							/>
							<UIKit.Text as="span" className="min-w-0 flex-1">
								{item.name}
							</UIKit.Text>
						</UIKit.Button>
					))}
				</div>
				{length && (
					<div className="flex flex-col gap-1 rounded-lg border border-border px-2.5 py-2 text-xs text-ink">
						<UIKit.Text as="span">
							По потолку: <b className="text-ink tabular-nums">{formatMeters(length.plan)}</b>
						</UIKit.Text>
						<UIKit.Text as="span">
							Спуски к приборам:{" "}
							<b className="text-ink tabular-nums">{formatMeters(length.drops)}</b>
						</UIKit.Text>
						<UIKit.Text as="span">
							Всего кабеля: <b className="text-ink tabular-nums">{formatMeters(length.total)}</b>
						</UIKit.Text>
					</div>
				)}
				<UIKit.Text className="text-xs leading-copy text-ink">
					Трасса идёт по потолку и спускается к прибору на стене на его высоту установки; к
					светильнику на потолке спуска нет.
				</UIKit.Text>
				<UIKit.Button
					type="button"
					className="mt-1 rounded-lg border border-red-200 bg-red-50 px-2.5 py-2 text-sm text-danger hover:bg-red-50"
					onClick={onDelete}
				>
					Удалить кабель
				</UIKit.Button>
			</>
		);
	}

	return null;
};

/** Список типов раздела каталога — сменить тип объекта, не переставляя его. */
export function CatalogTypeList({
	items,
	current,
	onPick,
}: {
	items: CatalogItem[];
	current: string;
	onPick: (itemId: string) => void;
}) {
	if (items.length === 0) return null;
	return (
		<>
			<UIKit.Text
				as="span"
				className="mt-1 text-xs font-semibold tracking-label text-ink uppercase"
			>
				Тип
			</UIKit.Text>
			<div className="flex flex-col gap-1">
				{items.map((item) => (
					<UIKit.Button
						key={item.id}
						type="button"
						className={`flex items-center gap-2 rounded-lg border border-transparent px-2.25 py-1.75 text-left text-sm text-ink hover:bg-[#f3f5f9]${
							current === item.id ? " border-accent bg-accent-soft text-accent" : ""
						}`}
						onClick={() => onPick(item.id)}
					>
						<UIKit.Text
							as="span"
							className="size-2.5 shrink-0 rounded-sm"
							style={{ background: item.color }}
						/>
						<UIKit.Text as="span" className="min-w-0 flex-1">
							{item.name}
						</UIKit.Text>
					</UIKit.Button>
				))}
			</div>
		</>
	);
}
